import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import DashboardModal from "../../modals/Hrms/DashboardModal";
import EmployeeDetailsCard from "../../modals/Hrms/EmployeeDetailsCard";
import {
 fetchLmsEmployees,
 syncLmsEmployeesToHrms,
 setPageNumber,
} from "../../redux/erpSlice";


const HrmsDashboard = () => {
 const dispatch = useDispatch();
 const { lmsEmployees, loading, totalCount, pageNumber, pageSize } = useSelector((state) => state.lmsEmployees);
 const [isModalOpen, setIsModalOpen] = useState(false);
 const [searchTerm, setSearchTerm] = useState("");
 const [locationFilter, setLocationFilter] = useState("");
 const [expandedRowIndex, setExpandedRowIndex] = useState(null);


 useEffect(() => {
   dispatch(fetchLmsEmployees({ pageNumber, pageSize }))
     .then((result) => {
       if (result.meta.requestStatus === "fulfilled") {
         dispatch(syncLmsEmployeesToHrms(result.payload.employees));
       }
     });
 }, [dispatch, pageNumber, pageSize]);


 // Calculate dynamic stats based on actual employee data
 const getEmployeeStats = () => {
   const totalEmployees = totalCount || 0;
   const registered = lmsEmployees.filter(emp => emp.isActive === true).length;
   const contractual = lmsEmployees.filter(emp => emp.userStatus === "Contractual").length;
   const suspended = lmsEmployees.filter(emp => emp.userStatus === "Suspended").length;
   const terminated = lmsEmployees.filter(emp => emp.userStatus === "Terminated").length;
   const resigned = lmsEmployees.filter(emp => emp.userStatus === "Resigned").length;


   return {
     totalEmployees,
     registered,
     contractual,
     suspended,
     terminated,
     resigned,
   };
 };


 // Calculate attendance stats (for today)
 const getAttendanceStats = () => {
   const totalEmployees = lmsEmployees.filter(emp => emp.isActive === true).length;
   // These would be calculated from actual attendance data
   // For now, using placeholder logic
   const present = 0; // Would come from attendance API
   const absent = 0; // Would come from attendance API
   const late = 0; // Would come from attendance API


   return { totalEmployees, present, absent, late };
 };


 // Calculate department-wise employee data
 const getDepartmentData = () => {
   const departments = {};
  
   lmsEmployees.forEach(emp => {
     const dept = emp.department || "Unassigned";
     if (!departments[dept]) {
       departments[dept] = {
         department: dept,
         total: 0,
         registered: 0,
         resigned: 0,
         terminated: 0,
       };
     }
     departments[dept].total += 1;
     if (emp.isActive) departments[dept].registered += 1;
     if (emp.userStatus === "Resigned") departments[dept].resigned += 1;
     if (emp.userStatus === "Terminated") departments[dept].terminated += 1;
   });


   return Object.values(departments);
 };


 // Toggle view for employee details
 const toggleView = (index) => {
   setExpandedRowIndex(expandedRowIndex === index ? null : index);
 };


 // Pagination handlers
 const handlePreviousPage = () => {
   if (pageNumber > 1) {
     dispatch(setPageNumber(pageNumber - 1));
   }
 };


 const handleNextPage = () => {
   const totalPages = Math.ceil(totalCount / pageSize);
   if (pageNumber < totalPages) {
     dispatch(setPageNumber(pageNumber + 1));
   }
 };


 const handlePageClick = (page) => {
   dispatch(setPageNumber(page));
 };


 // Calculate pagination info
 const totalPages = Math.ceil(totalCount / pageSize);
 const startItem = (pageNumber - 1) * pageSize + 1;
 const endItem = Math.min(pageNumber * pageSize, totalCount);


 // Generate page numbers for pagination
 const getPageNumbers = () => {
   const delta = 2;
   const range = [];
   const rangeWithDots = [];


   for (let i = Math.max(2, pageNumber - delta); i <= Math.min(totalPages - 1, pageNumber + delta); i++) {
     range.push(i);
   }


   if (pageNumber - delta > 2) {
     rangeWithDots.push(1, '...');
   } else {
     rangeWithDots.push(1);
   }


   rangeWithDots.push(...range);


   if (pageNumber + delta < totalPages - 1) {
     rangeWithDots.push('...', totalPages);
   } else {
     rangeWithDots.push(totalPages);
   }


   return rangeWithDots;
 };


 // Filter employees based on search and location
 const filteredEmployees = lmsEmployees.filter((employee) => {
   const fullName = `${employee.firstName} ${employee.lastName}`.toLowerCase();
   return (
     (fullName.includes(searchTerm.toLowerCase()) ||
       employee.email.includes(searchTerm)) &&
     (locationFilter === "" || employee.location === locationFilter)
   );
 });


 const stats = getEmployeeStats();
 const attendance = getAttendanceStats();
 const employeeData = getDepartmentData();
 return (
   <div className="p-3 sm:p-4 md:p-6 bg-gray-100 min-h-screen text-sm max-w-full overflow-x-hidden">
     {/* Loading State */}
     {loading && (
       <div className="flex justify-center items-center py-8">
         <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
         <span className="ml-2 text-purple-600">Loading dashboard...</span>
       </div>
     )}


     {/* Header Stats */}
     <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4 mb-4 md:mb-6">
       {[
         { label: "Total Emp.", value: stats.totalEmployees, color: "bg-green-100", icon: "👥" },
         { label: "Registered", value: stats.registered, color: "bg-blue-100", icon: "🧑‍💼" },
         { label: "Contractual", value: stats.contractual, color: "bg-purple-100", icon: "📝" },
         { label: "Suspended", value: stats.suspended, color: "bg-yellow-100", icon: "⏸" },
         { label: "Terminated", value: stats.terminated, color: "bg-orange-100", icon: "❌" },
         { label: "Resigned", value: stats.resigned, color: "bg-red-100", icon: "🚪" },
       ].map((stat, index) => (
         <div key={index} className={`p-2 sm:p-3 md:p-4 rounded-lg sm:rounded-xl shadow-md flex items-center justify-between sm:justify-start`}>
           <span className={`text-base sm:text-xl mr-2 sm:mr-3 p-1 sm:p-2 rounded-full ${stat.color}`}>{stat.icon}</span>
           <div className="flex-1">
             <p className="text-sm sm:text-base md:text-lg font-semibold">{stat.value}</p>
             <p className="text-xs sm:text-sm text-gray-600">{stat.label}</p>
           </div>
         </div>
       ))}
     </div>


     {/* Main Sections */}
     <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6 mb-4 md:mb-6">
       {/* Profile Section */}
       <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl shadow-md col-span-1">
         <h3 className="text-base sm:text-lg font-bold">Welcome Back 🎉</h3>
         <p className="text-xs sm:text-sm text-gray-600">View your overall report</p>
         <button
        // onClick={() => setIsModalOpen(true)}
         className="mt-3 sm:mt-4 px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg text-xs sm:text-sm"
       >
         User Dashboard
       </button>
       </div>


       {/* Attendance Section */}
       <div className="bg-white p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl shadow-md col-span-1 md:col-span-2">
         <h3 className="text-base sm:text-lg font-bold">Today's Employee Attendance</h3>
         <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-3 sm:mt-4">
           {[
             { label: "Total Employees", value: attendance.totalEmployees, icon: "👥" },
             { label: "Present", value: attendance.present, icon: "✅" },
             { label: "Absent", value: attendance.absent, icon: "❌" },
             { label: "Late", value: attendance.late, icon: "⏳" },
           ].map((item, i) => (
             <div key={i} className="flex items-center space-x-2">
               <span className="text-base sm:text-xl p-1 sm:p-2 bg-red-50 rounded-full">{item.icon}</span>
               <div>
                 <p className="text-sm sm:text-base md:text-lg font-semibold">{item.value}</p>
                 <p className="text-xs sm:text-sm text-gray-600">{item.label}</p>
               </div>
             </div>
           ))}
         </div>
       </div>
     </div>


     {/* Search and Filter Section */}
     <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4 w-full">
       <input
         type="text"
         className="p-2 border border-gray-300 rounded w-full sm:w-3/5 md:w-3/6 text-sm"
         placeholder="Search by name or email"
         value={searchTerm}
         onChange={(e) => setSearchTerm(e.target.value)}
       />
       <select
         className="p-2 border border-gray-300 rounded w-full sm:w-2/5 md:w-2/6 text-sm"
         value={locationFilter}
         onChange={(e) => setLocationFilter(e.target.value)}
       >
         <option value="">All Locations</option>
         {[...new Set(lmsEmployees.map((e) => e.location))].filter(loc => loc).map((loc, i) => (
           <option key={i} value={loc}>
             {loc}
           </option>
         ))}
       </select>
     </div>


     {/* Employee Details Table */}
     <div className="overflow-x-auto bg-white rounded-lg shadow-md -mx-3 sm:mx-0">
       <table className="min-w-full w-full">
         <thead className="border-b font-bold bg-gray-50">
           <tr>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left">Profile</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left whitespace-nowrap">Name</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left hidden lg:table-cell">Gender</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left">Email</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left hidden md:table-cell">Type</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left hidden xl:table-cell">Phone</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left hidden xl:table-cell">DOB</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left hidden lg:table-cell">Location</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left">Active</th>
             <th className="p-2 sm:p-3 text-xs sm:text-sm text-left">Actions</th>
           </tr>
         </thead>
         <tbody className="text-xs sm:text-sm">
           {!loading && filteredEmployees.map((employee, index) => (
             <React.Fragment key={employee.email}>
               <tr className="hover:bg-gray-50 border-b">
                 <td className="p-2 sm:p-3">
                   <img
                     src={employee.profilePicture || "https://via.placeholder.com/150"}
                     alt="Employee Profile"
                     className="w-8 h-8 rounded-full object-cover"
                   />
                 </td>
                 <td className="p-2 sm:p-3 whitespace-nowrap">{`${employee.firstName} ${employee.lastName}`}</td>
                 <td className="p-2 sm:p-3 hidden lg:table-cell">{employee.gender || "N/A"}</td>
                 <td className="p-2 sm:p-3 truncate max-w-[150px]">{employee.email}</td>
                 <td className="p-2 sm:p-3 hidden md:table-cell">{employee.userType || "N/A"}</td>
                 <td className="p-2 sm:p-3 text-blue-500 hidden xl:table-cell">{employee.phone || "N/A"}</td>
                 <td className="p-2 sm:p-3 hidden xl:table-cell whitespace-nowrap">{employee.dob ? new Date(employee.dob).toLocaleDateString() : "N/A"}</td>
                 <td className="p-2 sm:p-3 hidden lg:table-cell">{employee.location || "N/A"}</td>
                 <td className="p-2 sm:p-3">{employee.isActive ? "Yes" : "No"}</td>
                 <td className="p-2 sm:p-3">
                   <div
                     onClick={() => toggleView(index)}
                     className="cursor-pointer hover:bg-[#241763]/60 group bg-[#B6A5C9]/50 p-1.5 sm:p-2 rounded inline-block"
                   >
                     {expandedRowIndex === index ? (
                       <ChevronUp size={14} className="sm:w-4 sm:h-4 group-hover:text-white text-purple-600" />
                     ) : (
                       <ChevronDown size={14} className="sm:w-4 sm:h-4 group-hover:text-white text-purple-600" />
                     )}
                   </div>
                 </td>
               </tr>
               {expandedRowIndex === index && (
                 <tr>
                   <td colSpan="10" className="p-2 sm:p-4">
                     <div className="border rounded-lg bg-gray-50 shadow-inner p-3 sm:p-4">
                       <EmployeeDetailsCard employee={employee} />
                     </div>
                   </td>
                 </tr>
               )}
             </React.Fragment>
           ))}
         </tbody>
       </table>
     </div>


     {/* Pagination Component */}
     {!loading && totalCount > 0 && (
       <div className="flex flex-col gap-3 sm:gap-4 mt-4 sm:mt-6">
         {/* Top Row: Results Info and Page Size */}
         <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
           <div className="text-xs sm:text-sm text-gray-600">
             Showing {startItem} to {endItem} of {totalCount} results
           </div>
          
           {/* Page Size Selector */}
           <div className="flex items-center space-x-2">
             <span className="text-xs sm:text-sm text-gray-600">Show:</span>
             <select
               value={pageSize}
               onChange={(e) => {
                 const newPageSize = parseInt(e.target.value);
                 dispatch(setPageNumber(1));
                 dispatch(fetchLmsEmployees({ pageNumber: 1, pageSize: newPageSize }));
               }}
               className="border border-gray-300 rounded px-2 py-1 text-xs sm:text-sm"
             >
               <option value={5}>5</option>
               <option value={10}>10</option>
               <option value={20}>20</option>
               <option value={50}>50</option>
             </select>
             <span className="text-xs sm:text-sm text-gray-600">per page</span>
           </div>
         </div>


         {/* Bottom Row: Pagination Controls */}
         <div className="flex justify-center items-center overflow-x-auto">
           <div className="flex items-center space-x-1 sm:space-x-2">
             <button
               onClick={handlePreviousPage}
               disabled={pageNumber === 1}
               className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium ${
                 pageNumber === 1
                   ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                   : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
               }`}
             >
               <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
               <span className="hidden sm:inline">Previous</span>
             </button>


             <div className="flex items-center space-x-1">
               {totalPages > 1 && getPageNumbers().map((page, index) => (
                 <React.Fragment key={index}>
                   {page === '...' ? (
                     <span className="px-2 sm:px-3 py-1.5 sm:py-2 text-gray-500 text-xs sm:text-sm">...</span>
                   ) : (
                     <button
                       onClick={() => handlePageClick(page)}
                       className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium ${
                         page === pageNumber
                           ? 'bg-purple-600 text-white'
                           : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                       }`}
                     >
                       {page}
                     </button>
                   )}
                 </React.Fragment>
               ))}
             </div>


             <button
               onClick={handleNextPage}
               disabled={pageNumber === totalPages}
               className={`flex items-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-md text-xs sm:text-sm font-medium ${
                 pageNumber === totalPages
                   ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                   : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
               }`}
             >
               <span className="hidden sm:inline">Next</span>
               <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
             </button>
           </div>
         </div>
       </div>
     )}
     <DashboardModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
   </div>
 );
};


export default HrmsDashboard;
